<div class="flex flex-col h-full">
  <div class="flex w-full relative">
    @if (isLoading()) {
      <div class="w-full absolute">
        <mat-progress-bar
          data-cy="list-filters-loader"
          class="mds-x-small"
          [mode]="'indeterminate'"
        >
        </mat-progress-bar>
      </div>
    }
  </div>

  <div class="flex flex-row p-4 pr-6 border-b border-gray-1">
    <div class="h-8 flex flex-1 flex-wrap content-center font-semibold">
      {{ translations.title.text }}
    </div>
    <button
      id="options-panel-filters-close"
      class="text-gray-8"
      modus-icon-button
      [modus-tooltip]="translations.close.tooltip.text"
      (click)="close()"
    >
      <md-icon>close</md-icon>
    </button>
  </div>

  <div class="flex-1 overflow-y-scroll p-4" cdkScrollable [formGroup]="formGroup">
    <!-- name -->
    <modus-form-field>
      <modus-label>{{ translations.inputs.name.label.text }}</modus-label>
      <input
        modus-input
        data-cy="name-input"
        [formControl]="formGroup.controls.name"
        (keydown.enter)="applyFilters()"
      />
    </modus-form-field>

    <!-- status -->
    <label class="block text-small text-gray-8 py-1">
      {{ translations.inputs.status.label.text }}
    </label>
    <mat-form-field class="w-full mb-2" subscriptSizing="dynamic">
      <mat-select
        multiple
        disableRipple
        data-cy="status-select"
        class="modus-multi-select"
        panelClass="modus-multi-select-panel"
        [formControl]="formGroup.controls.status"
      >
        <mat-select-trigger>
          {{ selectedStatusLabel() }}
        </mat-select-trigger>
        @for (status of statusListOptions; track status.key) {
          <mat-option [value]="status.key">{{ status.displayValue }}</mat-option>
        }
      </mat-select>
    </mat-form-field>

    <!-- upload date -->
    <modus-date-picker
      data-cy="upload-date-picker"
      class="block mb-2"
      [label]="translations.inputs.uploadDate.label.text"
    >
      <modus-date-input
        data-cy="upload-from-date-input"
        type="start"
        format="yyyy-mm-dd"
        allowed-chars-regex="[\d-]"
        show-calendar-icon="true"
        [max]="maxDate()"
        [formControl]="formGroup.controls.uploadFromDate"
        [placeholder]="translations.inputs.uploadDate.fromPlaceholder.text"
      ></modus-date-input>
      <modus-date-input
        data-cy="upload-to-date-input"
        type="end"
        format="yyyy-mm-dd"
        allowed-chars-regex="[\d-]"
        show-calendar-icon="true"
        [max]="maxDate()"
        [formControl]="formGroup.controls.uploadToDate"
        [placeholder]="translations.inputs.uploadDate.toPlaceholder.text"
      ></modus-date-input>
    </modus-date-picker>

    <!-- capture date -->
    <modus-date-picker
      [label]="translations.inputs.captureDate.label.text"
      class="block mb-2"
      data-cy="capture-date-picker"
    >
      <modus-date-input
        data-cy="capture-from-date-input"
        type="start"
        format="yyyy-mm-dd"
        allowed-chars-regex="[\d-]"
        show-calendar-icon="true"
        [max]="maxDate()"
        [formControl]="formGroup.controls.captureFromDate"
        [placeholder]="translations.inputs.captureDate.fromPlaceholder.text"
      ></modus-date-input>
      <modus-date-input
        data-cy="capture-to-date-input"
        type="end"
        format="yyyy-mm-dd"
        allowed-chars-regex="[\d-]"
        show-calendar-icon="true"
        [max]="maxDate()"
        [formControl]="formGroup.controls.captureToDate"
        [placeholder]="translations.inputs.captureDate.toPlaceholder.text"
      ></modus-date-input>
    </modus-date-picker>

    <!-- scanner type -->
    <modus-autocomplete-input
      #scannerTypeAutocomplete
      data-cy="scanner-type-autocomplete"
      [label]="translations.inputs.scannerType.label.text"
      [clearable]="true"
      [options]="scannerTypeOptionList"
      [formControl]="formGroup.controls.scannerType"
      [placeholder]="translations.inputs.scannerType.placeholder.text"
      [modus-tooltip]="translations.inputs.scannerType.placeholder.text"
    >
    </modus-autocomplete-input>
    <modus-option-list
      #scannerTypeOptionList="modusOptionList"
      [noResultsFoundText]="translations.optionList.noResultsFound.text"
      [noResultsFoundSubtext]="translations.optionList.noResultsFoundHint.text"
    >
      @for (scannerType of filteredScannerTypes(); track scannerType) {
        <modus-option [value]="scannerType">{{ scannerType }}</modus-option>
      }
    </modus-option-list>

    <!-- tags chipset -->
    <label class="block text-small text-gray-8 pt-1">
      {{ translations.inputs.tags.label.text }}
    </label>
    <div class="flex justify-start flex-wrap">
      @for (tag of displayTags(); track tag.key) {
        <modus-chip
          show-close
          data-cy="tag-chip"
          class="m-1"
          [value]="tag.displayValue"
          (closeClick)="tagRemoved(tag.key)"
        >
        </modus-chip>
      }
    </div>

    <!-- tags autocomplete -->
    <!-- NB! Don't use (blur).
      Older code used the blur event to reset the FormControl when the control lost focus
      wihtout an autocomplete option being selected. modus-autocomplete does not fire the
      (optionSelected) event if (blur) has been handled. To stay true to the Modus paradigm,
      the user has to click the 'Clear' button to clear the input, otherwise we just leave it there.
      -->
    <modus-autocomplete-input
      #tagSelectorAutocomplete
      data-cy="tag-autocomplete"
      [clearable]="true"
      [options]="tagOptionList"
      [formControl]="formGroup.controls.tagSelector"
      [placeholder]="translations.inputs.tags.placeholder.text"
      [modus-tooltip]="translations.inputs.tags.placeholder.text"
      (optionSelected)="tagSelected($event)"
    >
    </modus-autocomplete-input>
    <modus-option-list
      #tagOptionList="modusOptionList"
      [noResultsFoundText]="translations.optionList.noResultsFound.text"
      [noResultsFoundSubtext]="translations.optionList.noResultsFoundHint.text"
    >
      @for (tag of filteredTags(); track tag.key) {
        <modus-option [value]="tag.key">{{ tag.displayValue }}</modus-option>
      }
    </modus-option-list>

    <div class="flex flex-col mb-1">
      <div class="flex items-center text-small text-gray-8">
        <md-icon class="!text-base text-blue-light mr-1">info_outlined</md-icon>
        {{ translations.inputs.tagsMatch.label.text }}
      </div>
      <mat-radio-group [formControl]="formGroup.controls.tagsMatchAll">
        <mat-radio-button color="primary" [value]="true" data-cy="and-radio">
          {{ translations.inputs.tagsMatch.optionAll.text }}
        </mat-radio-button>
        <mat-radio-button color="primary" class="ml-4" [value]="false" data-cy="or-radio">
          {{ translations.inputs.tagsMatch.optionAny.text }}
        </mat-radio-button>
      </mat-radio-group>
    </div>

    <!-- uploaded by -->
    <label class="block text-small text-gray-8 py-1">
      {{ translations.inputs.uploadedBy.label.text }}
    </label>
    <mat-form-field class="w-full mb-2" subscriptSizing="dynamic">
      <mat-select
        multiple
        disableRipple
        data-cy="uploaded-by-select"
        class="modus-multi-select"
        panelClass="modus-multi-select-panel"
        [formControl]="formGroup.controls.uploadedBy"
      >
        <mat-select-trigger>
          {{ selectedUploadedByLabel() }}
        </mat-select-trigger>
        @for (user of users(); track user.id) {
          <mat-option [value]="user.id">
            {{ user.fullName }}
          </mat-option>
        }
      </mat-select>
    </mat-form-field>

    <!-- classified -->
    <div class="pt-2">
      <modus-switch
        data-cy="classified-switch"
        [formControl]="formGroup.controls.isClassified"
        [label]="translations.inputs.hasClassifications.label.text"
      ></modus-switch>
    </div>

    <!-- station -->
    <div class="pt-2">
      <modus-switch
        data-cy="stations-switch"
        [formControl]="formGroup.controls.containsStations"
        [label]="translations.inputs.hasStations.label.text"
      ></modus-switch>
    </div>
  </div>

  <!-- button group -->
  <div class="flex p-4 justify-end border-t border-gray-1">
    <button
      id="options-panel-filters-clear"
      modus-button
      (click)="clearFilters()"
      [disabled]="isClearDisabled()"
      class="mr-4"
      color="secondary"
    >
      {{ translations.clear.text }}
    </button>

    <button
      id="options-panel-filters-apply"
      modus-flat-button
      (click)="applyFilters()"
      [disabled]="isApplyDisabled()"
    >
      {{ translations.apply.text }}
    </button>
  </div>
</div>
